import { Link } from '@tanstack/react-router';
import { Check, ChevronDown, ChevronRight, Clock, Minus, TriangleAlert, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { createContext, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Button } from '~/components/ui/button';
import type { ProductLookupRow } from '~/features/deliveries/types/delivery-runs.types';
import { cn } from '~/utils/cn';
import {
  formatDateTimeShort,
  formatTimeOfDay,
  isSameDay,
  diffInMs,
  nowISO,
  formatDayMonth,
} from '~/utils/date';

import {
  IMAGE_CHECK_FOLD_TARGET,
  IMAGE_CHECK_STAGE,
  isHiddenStage,
} from '../constants/hidden-stages';
import { useJourneyDeliveryOutcome } from '../hooks/use-journey-delivery-outcome';
import {
  type ProductRowFind,
  useProductEnrichedRow,
  useProductSampleRow,
} from '../hooks/use-product-stage-content';
import { useRunProductScoreDetails } from '../hooks/use-run-content';
import { useRunLabels } from '../hooks/use-run-labels';
import { useRunStageAttempts } from '../hooks/use-run-stage-attempts';
import type { LineageItem, Stage } from '../types/lineage-images.api.types';
import type {
  ProductScoreDetails,
  RunEnrichedProduct,
  SamplePreviewProduct,
} from '../types/run-content.api.types';
import type { StageAttempt } from '../types/run-detail.api.types';
import type { RunProgress } from '../types/run.api.types';
import { STOPPED_RECENT_DAYS } from '../utils/batches-table';
import { humanizeCode } from '../utils/catalog-labels';
import { enrichedFieldKind, isChangedKind, type EnrichedFieldKind } from '../utils/enriched-fields';
import { buildStageTimings } from '../utils/journey-timeline';
import { formatScope, itemStatusLabel, lineageReasonText } from '../utils/lineage-images-format';
import {
  batchReferenceReason,
  isStoppedRunStatus,
  JOURNEY_CONTENT_STAGES,
  JOURNEY_LEDGER_STAGES,
  ordinal,
  stageBarState,
  type StageBarState,
  stageProgressOf,
  stageRetryGroups,
  worseBarState,
} from '../utils/product-history';
import { gateRedirectSearch } from '../utils/run-decisions';
import { DASH, formatLift, formatScore } from '../utils/run-format';
import { fieldScoresAscending, HEADLINE_SCORE } from '../utils/run-score-fields';
import { skipReasonText } from '../utils/skip-code';

import { RunNameLink } from './run-name';
import { StageRetryNote } from './stage-retry-note';

/** How problems read on this journey: red only for a stop in the last week (the 7-day rule), and
 *  the step a reviewer rejected reads "Rejected", not "Failed". */
const JourneyToneCtx = createContext<{ alarm: boolean; rejectedStage: string | null }>({
  alarm: true,
  rejectedStage: null,
});

/** Glyph per ledger-item / coarse stage outcome — colour only for problems (G1). */
const STATUS_ICON: Record<string, { Icon: typeof Check; className: string }> = {
  SUCCESS: { Icon: Check, className: 'text-foreground' },
  COMPLETED: { Icon: Check, className: 'text-foreground' },
  FAILED: { Icon: X, className: 'text-error' },
  REJECTED: { Icon: X, className: 'text-error' },
  REVISIT: { Icon: TriangleAlert, className: 'text-warning' },
  RECONCILE_FAILED: { Icon: TriangleAlert, className: 'text-warning' },
  PENDING: { Icon: Clock, className: 'text-muted-foreground' },
  RUNNING: { Icon: Clock, className: 'text-muted-foreground' },
  AWAITING_GATE: { Icon: Clock, className: 'text-warning' },
  SKIPPED: { Icon: Minus, className: 'text-muted-foreground' },
};

/** One row's status icon + label, from a ledger item or a coarse StageProgress/StageAttempt status. */
function StatusGlyph({ status, label }: { status: string; label: string }) {
  const { alarm } = useContext(JourneyToneCtx);
  const style = STATUS_ICON[status] ?? STATUS_ICON.PENDING;
  const Icon = style?.Icon ?? Clock;
  const quiet = !alarm && (status === 'FAILED' || status === 'REJECTED');
  return (
    <span
      className={cn('inline-flex items-center gap-1', quiet ? 'text-foreground' : style?.className)}
      title={label}
    >
      <Icon aria-hidden="true" className="size-4" />
      <span className="text-sm">{label}</span>
    </span>
  );
}

/** Pill tone per market outcome — colour only for success / problems (G1), muted otherwise. */
function marketTone(status: string, alarm: boolean): { pill: string; text: string } {
  if (status === 'SUCCESS' || status === 'COMPLETED') {
    return { pill: 'bg-success-50 text-success', text: 'text-success' };
  }
  if ((status === 'FAILED' || status === 'REJECTED') && alarm) {
    return { pill: 'bg-error-50 text-error', text: 'text-error' };
  }
  return { pill: 'bg-neutral-30 text-muted-foreground', text: 'text-muted-foreground' };
}

/** "✓ UK en" — one market's newest outcome as a pill: country bold, language muted. */
function MarketPill({ market, status }: { market: string | null; status: string }) {
  const { alarm } = useContext(JourneyToneCtx);
  const style = STATUS_ICON[status] ?? STATUS_ICON.PENDING;
  const Icon = style?.Icon ?? Clock;
  const [country, language] = (market ?? '').split(' · ');
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-sm',
        marketTone(status, alarm).pill,
      )}
    >
      <Icon aria-hidden="true" className="size-3.5" />
      {country ? <span className="font-medium">{country}</span> : null}
      {language ? <span className="opacity-70">{language}</span> : null}
    </span>
  );
}

/**
 * A Journey step's header row: chevron, step name, its status (pills / score / glyph) and the
 * duration on the right. The whole row toggles the step's detail box and takes a soft fill while
 * open; a not-reached step reads muted.
 */
function StepHeader({
  label,
  status,
  durationText,
  isOngoing,
  expanded,
  onToggle,
  muted,
  trailing,
}: {
  label: string;
  status: ReactNode;
  durationText: string | null;
  isOngoing: boolean;
  expanded: boolean;
  onToggle: () => void;
  muted: boolean;
  /** Extra action beside the duration (the open gate's "Review ›"). */
  trailing?: ReactNode;
}) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg pr-3 transition-colors',
        expanded ? 'bg-neutral-30' : 'hover:bg-neutral-20',
      )}
    >
      <Button
        type="button"
        variant="ghost"
        onClick={onToggle}
        aria-expanded={expanded}
        className="h-auto min-w-0 flex-1 cursor-pointer justify-start gap-3 px-2 py-2 text-left font-normal whitespace-normal hover:bg-transparent"
      >
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'text-muted-foreground size-4 shrink-0 transition-transform',
            !expanded && '-rotate-90',
          )}
        />
        <span
          className={cn(
            'w-44 shrink-0 text-sm font-semibold',
            muted ? 'text-muted-foreground' : 'text-foreground',
          )}
        >
          {label}
        </span>
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{status}</span>
      </Button>
      {trailing}
      <span
        className={cn(
          'w-24 shrink-0 text-right text-sm tabular-nums',
          isOngoing ? 'text-warning' : 'text-muted-foreground',
        )}
      >
        {durationText
          ? isOngoing
            ? t('journey.waitingDuration', { time: durationText })
            : durationText
          : DASH}
      </span>
    </div>
  );
}

/** A step's open detail box: its own facts (started, duration, …) as a definition list, then any
 *  step-specific body below. */
function StepBody({
  facts,
  children,
}: {
  facts: { label: string; value: ReactNode }[];
  children?: ReactNode;
}) {
  return (
    <div className="bg-background mt-2 mb-1 ml-9 flex flex-col gap-3 rounded-lg border p-4 text-sm">
      {facts.length > 0 ? (
        <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2">
          {facts.map((fact) => (
            <div key={fact.label} className="contents">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className="text-foreground min-w-0 tabular-nums">{fact.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {children}
    </div>
  );
}

/** Started + Duration rows every step's detail box opens with (only the ones that are known). */
function useTimingFacts(startedAt: string | null, durationText: string | null) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  const facts: { label: string; value: ReactNode }[] = [];
  if (startedAt) {
    facts.push({
      label: t('journey.started'),
      value: `${formatDayMonth(startedAt)}, ${formatTimeOfDay(startedAt)}`,
    });
  }
  if (durationText) facts.push({ label: t('journey.duration'), value: durationText });
  return facts;
}

/** What a step's timing on the rail carries into its header and detail box. */
interface StepTiming {
  startedAt: string | null;
  durationText: string | null;
  isOngoing: boolean;
  /** "audit" / "batch" — the not-reached message names what this is. */
  noun: string;
}

/**
 * One attempt's outcome, oldest first: "1st ok" / "2nd failed (reason)" — or, for
 * SUPERSEDED_BY_LATER_RUN / RUN_PRODUCT_HELD, a clean phrase + batch link instead of ever
 * rendering `lineageReasonText`'s humanized-UUID text for those two codes (W3 owns the real fix
 * upstream in `utils/lineage-images-format.ts`; `batchReferenceReason` is the local stand-in).
 */
function AttemptPhrase({ item }: { item: LineageItem }) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  const { t: tLineage } = useTranslation('catalog-enrichment-lineage-images');
  if (item.status !== 'FAILED') {
    return <>{t('journey.attemptOk', { ordinal: ordinal(item.attempt) })}</>;
  }
  const ref = batchReferenceReason(item);
  if (ref) {
    return (
      <>
        {t('journey.attemptFailedNoReason', { ordinal: ordinal(item.attempt) })} (
        {t(ref.kind === 'held' ? 'journey.heldByBatch' : 'journey.supersededByBatch')}{' '}
        <RunNameLink runId={ref.runId} className="text-xs" />)
      </>
    );
  }
  const reason = lineageReasonText(tLineage, item);
  return (
    <>
      {reason
        ? t('journey.attemptFailed', { ordinal: ordinal(item.attempt), reason })
        : t('journey.attemptFailedNoReason', { ordinal: ordinal(item.attempt) })}
    </>
  );
}

/**
 * A ledger stage (Feed intake / Matching): exact per-product attempts from the lineage
 * read, GROUPED BY MARKET (ledger `scope`) — a run can carry more than one market listing for the
 * same product (e.g. UK + US), and each market has its own attempt sequence and final outcome.
 * The header line shows "UK · en ✓ · US · en ✓"; the muted ↻ attempts note (`StageRetryNote`,
 * shared with the journey drawer and product history card) appears only under a market that was a
 * REAL retry — more than one attempt and at least one failure (design round 2, item 4B) — never
 * the old "N attempts: 2nd ok, 2nd ok" line that mixed two markets' single attempts together.
 */
function LedgerStageSection({
  stage,
  items,
  foldedItems = [],
  reached,
  expanded,
  onToggle,
  destination,
  timing,
}: {
  stage: Stage;
  items: LineageItem[];
  /** Feed intake only: this product's hidden Image check attempts, listed "Product image · …"
   *  in the expanded list so a product stopped there still says why (hidden-stages.ts). */
  foldedItems?: LineageItem[];
  /** Finding #7 — true when the stage bar says this batch reached this stage (run-level status)
   *  even though `items` came back empty: an explicit "couldn't load" beats a flat "not reached". */
  reached: boolean;
  expanded: boolean;
  onToggle: () => void;
  /** This batch's resolved connector name, so DELIVERY would read "Publish to X" here too. */
  destination: string | null;
  timing: StepTiming;
}) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  const { t: tLineage } = useTranslation('catalog-enrichment-lineage-images');
  const { alarm } = useContext(JourneyToneCtx);
  const { stageLabel } = useRunLabels();
  const label = stageLabel(stage, destination);
  const facts = useTimingFacts(timing.startedAt, timing.durationText);

  const byScope = new Map<string, LineageItem[]>();
  for (const item of items) {
    byScope.set(item.scope, [...(byScope.get(item.scope) ?? []), item]);
  }
  const marketGroups = [...byScope.entries()].map(([scope, groupItems]) => {
    const sorted = [...groupItems].sort((a, b) => a.attempt - b.attempt);
    return { scope, sorted, newest: sorted[sorted.length - 1] };
  });
  const retryGroups = stageRetryGroups({ ledgerItems: items }, stage);

  // One shared outcome reads once after the pills ("✓ UK en  ✓ US en  Success"); mixed outcomes
  // are already told apart by the pills' colours.
  const outcomes = new Set(marketGroups.map((g) => g.newest?.status));
  const sharedStatus = outcomes.size === 1 ? marketGroups[0]?.newest?.status : undefined;
  // Attempt lines only earn space when something was retried or the hidden Image check left a trace.
  const showAttempts =
    foldedItems.length > 0 || marketGroups.some(({ sorted }) => sorted.length > 1);

  return (
    <div className="flex flex-col">
      <StepHeader
        label={label}
        muted={!reached}
        expanded={expanded}
        onToggle={onToggle}
        durationText={timing.durationText}
        isOngoing={timing.isOngoing}
        status={
          marketGroups.length === 0 ? (
            reached ? (
              <span className="text-muted-foreground text-sm">
                {t('journey.ledgerRowsUnavailable')}
              </span>
            ) : (
              <StatusGlyph status="PENDING" label={t('journey.notReached')} />
            )
          ) : (
            <>
              {marketGroups.map(({ scope, newest }) =>
                newest ? (
                  <MarketPill key={scope} market={formatScope(scope)} status={newest.status} />
                ) : null,
              )}
              {sharedStatus ? (
                <span className="text-muted-foreground text-sm">
                  {itemStatusLabel(tLineage, sharedStatus)}
                </span>
              ) : null}
            </>
          )
        }
      />
      {retryGroups.map((group) => (
        <div key={`${group.stage}|${group.scope}`} className="pl-9">
          <StageRetryNote group={group} stageLabel={label} />
        </div>
      ))}
      {expanded ? (
        !reached ? (
          <StepBody facts={[]}>
            <p className="text-muted-foreground">
              {t('journey.stepNotRun', { noun: timing.noun })}
            </p>
          </StepBody>
        ) : (
          <StepBody
            facts={[
              ...facts,
              ...marketGroups.flatMap(({ scope, newest }) =>
                newest
                  ? [
                      {
                        label: formatScope(scope) ?? label,
                        value: (
                          <span className={marketTone(newest.status, alarm).text}>
                            {itemStatusLabel(tLineage, newest.status)}
                          </span>
                        ),
                      },
                    ]
                  : [],
              ),
            ]}
          >
            {showAttempts ? (
              <ol className="text-muted-foreground flex flex-col gap-1 border-t pt-3 text-xs">
                {marketGroups.flatMap(({ scope, sorted }) => {
                  const market = formatScope(scope);
                  return sorted.map((item) => (
                    <li
                      key={`${scope}|${String(item.attempt)}|${item.createdAt}`}
                      className="tabular-nums"
                    >
                      {market ? `${market} · ` : ''}
                      {t('journey.attemptsFold', { count: 1 }).replace(':', '')} {item.attempt} ·{' '}
                      {formatDateTimeShort(item.createdAt)} · <AttemptPhrase item={item} />
                    </li>
                  ));
                })}
                {[...foldedItems]
                  .sort((a, b) => a.attempt - b.attempt)
                  .map((item) => {
                    const market = formatScope(item.scope);
                    return (
                      <li
                        key={`folded|${item.scope}|${String(item.attempt)}|${item.createdAt}`}
                        className="tabular-nums"
                      >
                        {t('journey.atImageCheck')} · {market ? `${market} · ` : ''}
                        {t('journey.attemptsFold', { count: 1 }).replace(':', '')} {item.attempt} ·{' '}
                        {formatDateTimeShort(item.createdAt)} · <AttemptPhrase item={item} />
                      </li>
                    );
                  })}
              </ol>
            ) : null}
          </StepBody>
        )
      ) : null}
    </div>
  );
}

/** One content-generation / delivery stage: coarse status always shown, exact figures and the
 *  stage-level attempt history fetched only once this section is expanded (bounded, user-initiated).
 *  ENRICHMENT shows counts + the hand-off id only — the full field diffs live in the page's own
 *  "Content generation changes" section above (moved there per design round 2, item 7), not
 *  duplicated inside this accordion. */
function ContentStageSection({
  runId,
  productId,
  feedProductId,
  stage,
  refIds,
  expanded,
  onToggle,
  destination,
  isOpenGate,
  progressStatus: rawProgressStatus,
  timing,
}: {
  runId: string;
  productId: string;
  feedProductId: string | null;
  stage: Stage;
  progressStatus: string | undefined;
  refIds: Record<string, string[]> | undefined;
  expanded: boolean;
  onToggle: () => void;
  /** This batch's resolved connector name, so DELIVERY reads "Publish to X" here. */
  destination: string | null;
  /** True when this is the stage the batch's open gate is waiting at — shows a visible
   *  "Review ›" link straight to the decision screen (CLARITY_BATCH_BRIEF.md S2's row action). */
  isOpenGate: boolean;
  timing: StepTiming;
}) {
  const { rejectedStage } = useContext(JourneyToneCtx);
  const progressStatus =
    rejectedStage === stage && rawProgressStatus === 'FAILED' ? 'REJECTED' : rawProgressStatus;
  const { t } = useTranslation('catalog-enrichment-product-master');
  const { statusLabel, stageLabel } = useRunLabels();
  const label = stageLabel(stage, destination);
  const facts = useTimingFacts(timing.startedAt, timing.durationText);
  const reached = !!progressStatus && stageBarState(progressStatus) !== 'pending';
  // Per-product reads behind the detail box wait until it's open on a step that actually ran.
  const open = expanded && reached;

  // Score stages are a single cheap per-product read — safe to fetch whenever the section is open,
  // not only on expand, so the collapsed row can show the real number.
  const isScoreStage = stage === 'PRE_ENRICH_SCORE' || stage === 'POST_ENRICH_SCORE';
  const score = useRunProductScoreDetails(
    runId,
    isScoreStage ? productId : null,
    stage === 'PRE_ENRICH_SCORE' ? 'PRE' : 'POST',
  );
  // The "after" score row compares itself with "before" — the same cheap read, cached per product.
  const before = useRunProductScoreDetails(
    runId,
    stage === 'POST_ENRICH_SCORE' ? productId : null,
    'PRE',
  );
  const sample = useProductSampleRow(runId, productId, open && stage === 'SAMPLE');
  const enriched = useProductEnrichedRow(runId, productId, open && stage === 'ENRICHMENT');
  const delivery = useJourneyDeliveryOutcome(
    { runId, feedProductId },
    open && stage === 'DELIVERY',
  );
  const attempts = useRunStageAttempts(runId, stage, open);

  // `refIds` never carries a `generationRunId` key (the allowlist is syncId / batchId /
  // externalJobRef / deliveryStatus / …) — that fallback was dead; batchId / externalJobRef cover
  // every hand-off id this allowlist actually sends.
  const handoffId = refIds?.batchId?.[0] ?? refIds?.externalJobRef?.[0] ?? null;

  const overallScore = (values: typeof score.data) => {
    const overall = values?.scores?.find((s) => s.name === HEADLINE_SCORE)?.value;
    return overall ?? values?.product?.overallScore ?? null;
  };

  const scoreValue = isScoreStage ? overallScore(score.data) : null;
  const beforeValue = stage === 'POST_ENRICH_SCORE' ? overallScore(before.data) : null;

  // The header's status: a score with its bar (and "after" vs "before"), else the run's own
  // per-stage status — the exact per-product figures load once the box is opened.
  const status = (() => {
    if (isScoreStage && scoreValue !== null) {
      const delta = beforeValue !== null ? scoreValue - beforeValue : null;
      return (
        <>
          <span className="inline-flex items-center gap-1.5 text-sm tabular-nums">
            <Check aria-hidden="true" className="text-foreground size-4" />
            <span className="text-foreground font-semibold">{formatScore(scoreValue)}</span>
            <span className="text-muted-foreground">/ 100</span>
          </span>
          <span
            role="img"
            aria-label={t('journey.scoreValue', { value: formatScore(scoreValue) })}
            className="bg-muted flex h-2 w-40 overflow-hidden rounded-full"
          >
            <span
              className="bg-success h-full"
              style={{ width: `${String(Math.max(0, Math.min(100, scoreValue)))}%` }}
            />
          </span>
          {delta !== null ? (
            <span className="text-muted-foreground text-sm">
              {Math.abs(delta) < 0.05
                ? t('journey.scoreNoChange')
                : t('journey.scoreChange', {
                    delta: `${delta > 0 ? '+' : '−'}${formatScore(Math.abs(delta))}`,
                  })}
            </span>
          ) : null}
        </>
      );
    }
    if (isScoreStage && reached) {
      return (
        <StatusGlyph
          status={progressStatus}
          label={score.isError ? t('journey.readError') : t('journey.scoreNotScored')}
        />
      );
    }
    return (
      <StatusGlyph
        status={progressStatus ?? 'PENDING'}
        label={reached && progressStatus ? statusLabel(progressStatus) : t('journey.notReached')}
      />
    );
  })();

  return (
    <div className="flex flex-col">
      <StepHeader
        label={label}
        muted={!reached}
        expanded={expanded}
        onToggle={onToggle}
        durationText={timing.durationText}
        isOngoing={timing.isOngoing}
        status={status}
        trailing={
          isOpenGate ? (
            <Link
              to="/catalog-enrichment/runs/$runId/products"
              params={{ runId }}
              search={gateRedirectSearch(stage, productId, null)}
              className="text-primary shrink-0 cursor-pointer text-sm whitespace-nowrap hover:underline"
            >
              {t('journey.reviewNow')} ›
            </Link>
          ) : null
        }
      />

      {expanded ? (
        !reached ? (
          <StepBody facts={[]}>
            <p className="text-muted-foreground">
              {t('journey.stepNotRun', { noun: timing.noun })}
            </p>
          </StepBody>
        ) : (
          <StepBody
            facts={[
              ...facts,
              ...(handoffId
                ? [
                    {
                      label: t('journey.handoffIdLabel'),
                      value: (
                        <span className="inline-flex items-center gap-1 font-mono text-xs">
                          {handoffId}
                          <CopyIconButton text={handoffId} label={t('journey.copyId')} />
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
          >
            {isScoreStage ? <ScoreBody data={score.data} isError={score.isError} /> : null}

            {stage === 'SAMPLE' ? <SampleBody find={sample.data} isError={sample.isError} /> : null}

            {stage === 'ENRICHMENT' ? (
              <>
                <EnrichmentCountsBody find={enriched.data} isError={enriched.isError} />
                <JourneyCompareLink runId={runId} productId={productId} />
              </>
            ) : null}

            {stage === 'APPROVAL' ? (
              <ApprovalBody attempts={attempts.data?.attempts} isError={attempts.isError} />
            ) : null}

            {stage === 'DELIVERY' ? (
              <>
                <DeliveryBody status={delivery.status} rows={delivery.rows} />
                <JourneyCompareLink runId={runId} productId={productId} />
              </>
            ) : null}

            {!isScoreStage && stage !== 'APPROVAL' ? (
              <StageAttemptsList attempts={attempts.data?.attempts} isError={attempts.isError} />
            ) : null}
          </StepBody>
        )
      ) : null}
    </div>
  );
}

/** PRE / POST score expanded body: weakest field and any failure the score read carries. */
function ScoreBody({
  data,
  isError,
}: {
  data: ProductScoreDetails | null | undefined;
  isError: boolean;
}) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  const { fieldLabel } = useRunLabels();
  if (isError) return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
  if (!data) return <p className="text-muted-foreground text-xs">{t('journey.scoreNotScored')}</p>;
  const record = Object.fromEntries((data.scores ?? []).map((s) => [s.name, s.value]));
  const weakest = fieldScoresAscending(record)[0];
  return (
    <div className="flex flex-col gap-1 text-xs">
      {weakest ? <p>{t('journey.scoreWeakest', { field: fieldLabel(weakest.name) })}</p> : null}
      {data.product?.errorMessage ? (
        <p className="text-error">{data.product.errorMessage}</p>
      ) : null}
    </div>
  );
}

/** Sample stage expanded body: in/out of sample, the lift and any per-product score error. */
function SampleBody({
  find,
  isError,
}: {
  find: ProductRowFind<SamplePreviewProduct> | undefined;
  isError: boolean;
}) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  if (isError) return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
  if (!find) return null; // still walking the sample pages
  const row = find.row;
  if (!row) {
    return (
      <p className="text-muted-foreground text-xs">
        {find.truncated
          ? t('journey.sampleTruncated', { scanned: find.scanned })
          : t('journey.sampleNotIn')}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1 text-xs">
      <p>
        {t('journey.sampleIn')}
        {row.lift !== null ? ` · ${t('journey.sampleLift', { lift: formatLift(row.lift) })}` : ''}
      </p>
      {row.errorMessage ? <p className="text-error">{row.errorMessage}</p> : null}
    </div>
  );
}

/** Enrichment stage's Journey-row body: counts only (data in → out), no field diffs — those are
 *  the page's own "Content generation changes" section (`EnrichmentChangesSection` below), so a
 *  product's changed fields are shown exactly once per page, not duplicated in the accordion. */
export function EnrichmentCountsBody({
  find,
  isError,
}: {
  find: ProductRowFind<RunEnrichedProduct> | undefined;
  isError: boolean;
}) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  const { t: tCg } = useTranslation('catalog-enrichment-content-gen');
  if (isError) return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
  if (!find) return null;
  const row = find.row;
  if (!row) {
    return (
      <p className="text-muted-foreground text-xs">
        {find.truncated
          ? t('journey.enrichmentTruncated', { scanned: find.scanned })
          : t('journey.sampleNotIn')}
      </p>
    );
  }
  // R12 — a product the run's scope left out says why, as a label (never the raw skip text).
  const skipped = skipReasonText(tCg, row.skipCode, row.errorMessage);
  if (skipped) return <p className="text-muted-foreground text-xs">{skipped}</p>;
  const counts = row.fields.reduce(
    (acc, field) => {
      const kind: EnrichedFieldKind = enrichedFieldKind(field);
      if (kind === 'failed') acc.failed += 1;
      else if (isChangedKind(kind)) acc.changed += 1;
      else acc.unchanged += 1;
      return acc;
    },
    { changed: 0, unchanged: 0, failed: 0 },
  );
  return (
    <p className="text-muted-foreground text-xs">
      {t('journey.enrichmentSummary', {
        changed: counts.changed,
        unchanged: counts.unchanged,
        failed: counts.failed,
      })}
    </p>
  );
}

/** Approval stage expanded body: the gate decision that closed it, from the stage's own attempts
 *  (no per-product approval read exists — a run's approval gate decides the whole run at once). */
function ApprovalBody({
  attempts,
  isError,
}: {
  attempts: StageAttempt[] | undefined;
  isError: boolean;
}) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  if (isError) return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
  // Attempts arrive oldest-first — the LATEST decided attempt wins after a retry re-decides.
  const decided = [...(attempts ?? [])].reverse().find((a) => a.gateOutcome);
  if (!decided) {
    return <p className="text-muted-foreground text-xs">{t('journey.approvalNotDecided')}</p>;
  }
  return (
    <p className="text-xs">
      {decided.gateOutcome === 'APPROVE'
        ? t('journey.approvalApproved')
        : t('journey.approvalRejected')}
    </p>
  );
}

/** Delivery stage expanded body: every destination this product's source id fanned out to (never
 *  just the first row — one source id can reach several destinations). */
function DeliveryBody({
  status,
  rows,
}: {
  status: 'idle' | 'loading' | 'no-job' | 'error' | 'no-connection' | 'not-found' | 'found';
  rows: ProductLookupRow[];
}) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  switch (status) {
    case 'no-job':
      return <p className="text-muted-foreground text-xs">{t('journey.deliveryNoJob')}</p>;
    case 'error':
      return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
    // Resolving the connection id is still a read in flight — never say "not available" for it.
    case 'idle':
    case 'loading':
      return <p className="text-muted-foreground text-xs">{t('journey.deliveryResolving')}</p>;
    case 'no-connection':
      return <p className="text-muted-foreground text-xs">{t('journey.deliveryNoConnection')}</p>;
    case 'not-found':
      return <p className="text-muted-foreground text-xs">{t('journey.deliveryNotSent')}</p>;
    case 'found':
      return (
        <ul className="flex flex-col gap-1.5 text-xs">
          {rows.map((row, i) => (
            <li key={row.id ?? i} className="flex flex-col gap-0.5">
              {row.destinationType ? (
                <span className="text-foreground font-medium">
                  {humanizeCode(row.destinationType)}
                </span>
              ) : null}
              <span>{humanizeCode(row.outcome ?? row.syncStatus ?? '')}</span>
              {(() => {
                // The wire sends "" rather than omitting a field — treat it as absent too.
                const detail = [row.errorMessage, row.syncStatusReason, row.reasonLabel].find(
                  (v) => !!v,
                );
                return detail ? <span className="text-error">{detail}</span> : null;
              })()}
            </li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}

/** This stage's own retry history (run-wide — the stage was retried as a whole, this product
 *  included), oldest first; hidden with ≤1 attempt since the row above already says the outcome. */
function StageAttemptsList({
  attempts,
  isError,
}: {
  attempts: StageAttempt[] | undefined;
  isError: boolean;
}) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  const { statusLabel } = useRunLabels();
  if (isError) return <p className="text-muted-foreground text-xs">{t('journey.readError')}</p>;
  if (!attempts || attempts.length <= 1) return null;
  return (
    <ol className="text-muted-foreground flex flex-col gap-0.5 text-xs">
      {attempts.map((a) => (
        <li key={a.attempt} className="tabular-nums">
          {a.attempt} · {statusLabel(a.status)}
          {a.startedAt ? ` · ${formatDateTimeShort(a.startedAt)}` : ''}
          {a.failureReason ? ` — ${a.failureReason}` : ''}
        </li>
      ))}
    </ol>
  );
}

/** Rail dot fill per bar state — only `current` (amber) / `failed` (red) carry a problem/attention
 *  colour (G1), matching `StageSegmentBar`'s palette so the two timelines read as one language. */
const DOT_CLASS: Record<StageBarState, string> = {
  pending: 'bg-background border-2 border-muted-foreground/40',
  done: 'bg-foreground/70',
  skipped: 'bg-foreground/70',
  current: 'bg-warning',
  failed: 'bg-error',
};

/** The rail segment connecting one dot to the next: solid once the stage behind it finished
 *  (red when it failed), dotted while still current/ahead — "solid rail done, dotted waiting/ahead"
 *  (design round 3, S2). */
function edgeLineClass(state: StageBarState, alarm = true): string {
  if (state === 'failed' && alarm) return 'border-l-2 border-solid border-error/50';
  if (state === 'failed') return 'border-l-2 border-solid border-foreground/30';
  if (state === 'done' || state === 'skipped')
    return 'border-l-2 border-solid border-foreground/30';
  return 'border-l-2 border-dashed border-muted-foreground/30';
}

/**
 * One stage's slot on the Journey rail: the product's own clock time on the left, a status dot
 * lined up with the step's header (solid rail behind done steps, dashed ahead), and the step itself.
 * A small amber ring marks a stage that was genuinely retried (design round 3, S2).
 */
function TimelineRow({
  state,
  hasRetry,
  topLine,
  bottomLine,
  timeLabel,
  children,
}: {
  state: StageBarState;
  hasRetry: boolean;
  /** Line class for the segment above this dot, or `null` on the first row (nothing to draw). */
  topLine: string | null;
  /** Line class for the segment below this dot, or `null` on the last row. */
  bottomLine: string | null;
  timeLabel: string | null;
  children: ReactNode;
}) {
  const { alarm } = useContext(JourneyToneCtx);
  return (
    <li className="flex gap-3">
      <span className="text-muted-foreground w-16 shrink-0 pt-2 text-right text-sm tabular-nums">
        {timeLabel ?? DASH}
      </span>
      <div aria-hidden="true" className="flex w-3 shrink-0 flex-col items-center">
        {/* Fixed-height top segment so the dot sits level with the header, open or closed. */}
        <span className={cn('h-3 w-0 shrink-0', topLine ?? 'invisible border-l-2')} />
        <span
          className={cn(
            'ring-background size-3 shrink-0 rounded-full ring-4',
            state === 'failed' && !alarm ? DOT_CLASS.done : DOT_CLASS[state],
            hasRetry && 'ring-warning ring-2',
          )}
        />
        <span className={cn('w-0 flex-1', bottomLine ?? 'invisible border-l-2')} />
      </div>
      <div className="min-w-0 flex-1 pb-2">{children}</div>
    </li>
  );
}

/** A day divider on the rail ("30 Oct" + a rule) — shown whenever the date changes between steps;
 *  the rail keeps running through it. */
function TimelineDateRow({ date, line }: { date: string; line: string | null }) {
  return (
    <li className="flex gap-3" aria-hidden="true">
      <span className="text-foreground w-16 shrink-0 py-2 text-right text-xs font-semibold">
        {date}
      </span>
      <div className="flex w-3 shrink-0 flex-col items-center">
        <span className={cn('w-0 flex-1', line ?? 'invisible border-l-2')} />
      </div>
      <div className="flex min-w-0 flex-1 items-center">
        <span className="w-full border-t" />
      </div>
    </li>
  );
}

/** The trailing not-reached stages as one "Up next" line: dashed chips in order, then why
 *  (design round 3, S2 — "Not-reached stages collapse into one muted line"). */
function TimelineNotReachedRow({
  topLine,
  labels,
  stoppedAtLabel,
}: {
  topLine: string | null;
  labels: string[];
  stoppedAtLabel: string | null;
}) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  return (
    <li className="flex gap-3" data-testid="catalog-enrichment-journey-up-next">
      <span className="w-16 shrink-0" />
      <div aria-hidden="true" className="flex w-3 shrink-0 flex-col items-center">
        <span className={cn('h-3 w-0 shrink-0', topLine ?? 'invisible border-l-2')} />
        <span
          className={cn('ring-background size-3 shrink-0 rounded-full ring-4', DOT_CLASS.pending)}
        />
      </div>
      <p className="text-muted-foreground flex min-w-0 flex-1 flex-wrap items-center gap-1.5 px-2 text-sm">
        <span className="mr-1">{t('journey.upNext')}</span>
        {labels.map((label, i) => (
          <span key={label} className="inline-flex items-center gap-1.5">
            {i > 0 ? <ChevronRight aria-hidden="true" className="size-3.5" /> : null}
            <span className="rounded-md border border-dashed px-2 py-0.5">{label}</span>
          </span>
        ))}
        <span className="ml-1 text-xs">
          {stoppedAtLabel
            ? t('journey.notReachedStoppedSuffix', { stage: stoppedAtLabel })
            : t('journey.notReachedSuffix')}
        </span>
      </p>
    </li>
  );
}

export interface ProductJourneyDetailProps {
  runId: string;
  productId: string;
  feedProductId: string | null;
  /** This product's ledger items for THIS run only (IMPORT / IMAGE_GATE / CURATION, every attempt). */
  runItems: LineageItem[];
  progress: RunProgress | undefined;
  /** This batch's resolved connector name, so DELIVERY reads "Publish to X" here. */
  destination: string | null;
  /** Opens this stage's row expanded on first render (e.g. a step the caller already knows about). */
  initialStage?: Stage | undefined;
  readError?: boolean;
}

/** Every stage the Journey rail can show, in flow order (ledger stages first, matching
 *  `flowStagesFor`'s default — the 9-stage list every flow in practice follows), minus the hidden
 *  Image check stage, which folds into Feed intake's row instead (hidden-stages.ts). */
const ALL_JOURNEY_STAGES: readonly Stage[] = [
  ...JOURNEY_LEDGER_STAGES,
  ...JOURNEY_CONTENT_STAGES,
].filter((stage) => !isHiddenStage(stage));
const LEDGER_STAGE_SET = new Set<Stage>(JOURNEY_LEDGER_STAGES);

/** This product's newest ledger state at one stage, else the run-level stage status. */
function ledgerRowState(
  stage: Stage,
  runItems: LineageItem[],
  progress: RunProgress | undefined,
): StageBarState {
  const newest = [...runItems]
    .filter((i) => i.stage === stage)
    .sort((a, b) => a.attempt - b.attempt)
    .at(-1);
  return stageBarState(newest?.status ?? stageProgressOf(progress, stage)?.status);
}

/**
 * The Journey section of the product detail page (design round 3, S2 — "activity timeline"):
 * every stage of this batch on a vertical rail, with its start time and duration, every attempt,
 * data in → out, delivery rows — the full detail the journey drawer used to show inline before it
 * became a one-glance summary. Any number of rows can be open at once (Expand all / Collapse all);
 * ENRICHMENT shows counts only here (its field diffs live in the page's own Content generation
 * changes section). Trailing stages this batch hasn't reached yet collapse into one muted line.
 */
export function ProductJourneyDetail({
  runId,
  productId,
  feedProductId,
  runItems,
  progress,
  destination,
  initialStage,
  readError = false,
}: ProductJourneyDetailProps) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  const { stageLabel } = useRunLabels();
  const [expanded, setExpanded] = useState<Set<Stage>>(
    () => new Set<Stage>(initialStage ? [initialStage] : []),
  );

  if (readError) {
    return <p className="text-muted-foreground py-4 text-sm">{t('journey.readError')}</p>;
  }
  const endedAt = progress?.completedAt ?? null;
  const alarm = !endedAt || diffInMs(endedAt, nowISO()) < STOPPED_RECENT_DAYS * 24 * 60 * 60 * 1000;
  const rejectedStage =
    progress?.failureCode === 'OPERATOR_REJECTED' ? (progress.currentStage ?? null) : null;

  const timings = buildStageTimings(ALL_JOURNEY_STAGES, runItems, progress);
  const rows = ALL_JOURNEY_STAGES.map((stage) => {
    if (LEDGER_STAGE_SET.has(stage)) {
      // Same fallback chain the journey drawer's stage bar uses: this product's own newest
      // ledger item, else the run-level stage status — so the dot agrees with the bar even
      // when `items` is empty because this product's own rows failed to load.
      const own = ledgerRowState(stage, runItems, progress);
      if (stage !== IMAGE_CHECK_FOLD_TARGET || own !== 'done') return { stage, state: own };
      // Feed intake finished: the hidden Image check's outcome is now part of this row.
      const folded = ledgerRowState(IMAGE_CHECK_STAGE, runItems, progress);
      return { stage, state: worseBarState(own, folded) };
    }
    return { stage, state: stageBarState(stageProgressOf(progress, stage)?.status) };
  });

  // Trailing run of not-yet-reached stages collapses into one muted line (design round 3, S2).
  let splitIndex = rows.length;
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rows[i]?.state !== 'pending') break;
    splitIndex = i;
  }
  const visibleRows = rows.slice(0, splitIndex);
  const notReachedRows = rows.slice(splitIndex);

  // Each row's date/retry/line metadata, computed once up front (plain loop, not inside the JSX
  // map below) so "date shown only when it changes" can track the previous row without mutating a
  // variable from inside a render-time callback.
  const rowMeta: {
    stage: Stage;
    state: StageBarState;
    startedAt: string | null;
    durationText: string | null;
    isOngoing: boolean;
    showDate: boolean;
    hasRetry: boolean;
  }[] = [];
  let lastShownDate: string | null = null;
  for (const { stage, state } of visibleRows) {
    const timing = timings.get(stage);
    const startedAt = timing?.startedAt ?? null;
    // The date only repeats on screen when it actually changes from the row above — a batch
    // that runs well past midnight still reads as one continuous timeline otherwise.
    const showDate = !!startedAt && (!lastShownDate || !isSameDay(startedAt, lastShownDate));
    if (startedAt) lastShownDate = startedAt;
    rowMeta.push({
      stage,
      state,
      startedAt,
      durationText: timing?.durationText ?? null,
      isOngoing: !!timing?.isOngoing,
      showDate,
      hasRetry:
        LEDGER_STAGE_SET.has(stage) &&
        stageRetryGroups({ ledgerItems: runItems }, stage).length > 0,
    });
  }

  const toggle = (stage: Stage) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  };
  const expandAll = () => {
    setExpanded(new Set(visibleRows.map((r) => r.stage)));
  };
  const collapseAll = () => {
    setExpanded(new Set());
  };

  const stoppedAtLabel = isStoppedRunStatus(progress?.runStatus)
    ? stageLabel(progress?.currentStage, destination)
    : null;
  const doneCount = rows.filter((r) => r.state === 'done' || r.state === 'skipped').length;
  const noun = t(progress?.flow === 'FEED_AUDIT' ? 'journey.nounAudit' : 'journey.nounBatch');

  return (
    <JourneyToneCtx.Provider value={{ alarm, rejectedStage }}>
      <div className="flex flex-col gap-3" data-testid="catalog-enrichment-journey">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="flex items-baseline gap-2">
            <span className="text-foreground text-base font-semibold">
              {t('journey.journeySection')}
            </span>
            <span
              className="text-muted-foreground text-sm tabular-nums"
              data-testid="catalog-enrichment-journey-steps-value"
            >
              {t('journey.stepsDone', { done: doneCount, total: rows.length })}
            </span>
          </h2>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={expandAll}
              disabled={visibleRows.length === 0}
              className="cursor-pointer disabled:cursor-not-allowed"
              data-testid="catalog-enrichment-journey-expand-all-button"
            >
              {t('journey.expandAll')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={collapseAll}
              disabled={expanded.size === 0}
              className="cursor-pointer disabled:cursor-not-allowed"
              data-testid="catalog-enrichment-journey-collapse-all-button"
            >
              {t('journey.collapseAll')}
            </Button>
          </div>
        </div>
        <ol className="flex flex-col">
          {rowMeta.map(
            ({ stage, state, startedAt, durationText, isOngoing, showDate, hasRetry }, index) => {
              const topLine =
                index > 0 ? edgeLineClass(rowMeta[index - 1]?.state ?? 'pending', alarm) : null;
              const timing: StepTiming = { startedAt, durationText, isOngoing, noun };
              return (
                <Fragment key={stage}>
                  {showDate && startedAt ? (
                    <TimelineDateRow date={formatDayMonth(startedAt)} line={topLine} />
                  ) : null}
                  <TimelineRow
                    state={state}
                    hasRetry={hasRetry}
                    topLine={topLine}
                    bottomLine={
                      index < rowMeta.length - 1 || notReachedRows.length > 0
                        ? edgeLineClass(state, alarm)
                        : null
                    }
                    timeLabel={startedAt ? formatTimeOfDay(startedAt) : null}
                  >
                    {LEDGER_STAGE_SET.has(stage) ? (
                      <LedgerStageSection
                        stage={stage}
                        items={runItems.filter((i) => i.stage === stage)}
                        foldedItems={
                          stage === IMAGE_CHECK_FOLD_TARGET
                            ? runItems.filter((i) => i.stage === IMAGE_CHECK_STAGE)
                            : []
                        }
                        reached={state !== 'pending'}
                        expanded={expanded.has(stage)}
                        onToggle={() => {
                          toggle(stage);
                        }}
                        destination={destination}
                        timing={timing}
                      />
                    ) : (
                      <ContentStageSection
                        runId={runId}
                        productId={productId}
                        feedProductId={feedProductId}
                        stage={stage}
                        progressStatus={stageProgressOf(progress, stage)?.status}
                        refIds={stageProgressOf(progress, stage)?.refIds}
                        expanded={expanded.has(stage)}
                        onToggle={() => {
                          toggle(stage);
                        }}
                        destination={destination}
                        isOpenGate={progress?.pendingGate?.stage === stage}
                        timing={timing}
                      />
                    )}
                  </TimelineRow>
                </Fragment>
              );
            },
          )}
          {notReachedRows.length > 0 ? (
            <TimelineNotReachedRow
              topLine={
                rowMeta.length > 0 ? edgeLineClass(rowMeta.at(-1)?.state ?? 'pending', alarm) : null
              }
              labels={notReachedRows.map((r) => stageLabel(r.stage, destination))}
              stoppedAtLabel={stoppedAtLabel}
            />
          ) : null}
        </ol>
      </div>
    </JourneyToneCtx.Provider>
  );
}

/** DOM id of the product page's "Content generation changes" section — the field-by-field
 *  source → generated → sent table (it replaced the separate Compare page). */
export const CONTENT_CHANGES_ANCHOR = 'content-changes';

/** "Field-by-field changes ›" from the journey drawer — opens this product's page at its
 *  source → generated → sent table, the one place that comparison now lives. */
export function JourneyCompareLink({ runId, productId }: { runId: string; productId: string }) {
  const { t } = useTranslation('catalog-enrichment-product-master');
  return (
    <Link
      to="/catalog-enrichment/runs/$runId/products/$productId"
      params={{ runId, productId }}
      hash={CONTENT_CHANGES_ANCHOR}
      className="text-primary w-fit cursor-pointer text-xs hover:underline"
    >
      {t('journey.compareSourceGenerated')}
    </Link>
  );
}
